import type { PickupAvailabilitySlot } from '../../../../../core/api/pickup/models/pickup.model';

/** Locale codes used when formatting pickup date/time labels. */
export type PickupDisplayLocale = 'en' | 'fr';

/** One selectable pickup date derived from a validate `pickUps` slot. */
export interface PickupDateOption {
  date: string;
  label: string;
  isToday: boolean;
}

/** Result of mapping validate slots into UI date selection defaults. */
export interface PickupDateSelection {
  dates: PickupDateOption[];
  selectedDate: string | null;
  windowFrom: string | null;
  windowTo: string | null;
}

/** Time select option with HHmm value and localized 12h label. */
export interface PickupTimeOption {
  value: string;
  label: string;
}

/** Inputs for building From/To dropdown options for a selected day. */
export interface BuildPickupWindowTimeOptionsInput {
  latestPickupStartTime: string;
  pickupWindowEndTime: string;
  selectedFrom: string;
  /** Minimum window duration in minutes from Salesforce (`cutOff` / cutoffWindowMins). */
  minWindowMinutes: number;
  /** Earliest selectable From (HHmm), e.g. same-day local time rounded up. */
  earliestFromHhmm?: string;
  locale?: PickupDisplayLocale;
}

/** Inputs for seeding date/window defaults from a validate slot. */
export interface SelectPickupDateDefaultsOptions {
  todayIso: string;
  /** Current Ship From local time as HHmm (province timezone). */
  currentLocalHhmm: string;
}

export type { ShipFromLocalClockOptions } from '../../../../../shared/utils/ship-from-local-clock';
export {
  formatCurrentLocalHhmm,
  formatCurrentLocalTime,
  formatLocalIsoDate,
  resolveCanadianProvinceTimeZone,
} from '../../../../../shared/utils/ship-from-local-clock';

/** Result of reconciling a From/To selection against available options. */
export interface PickupWindowSelectionResult {
  from: string | null;
  to: string | null;
  hasValidWindow: boolean;
}

const TIME_STEP_MINUTES = 15;
const EARLIEST_READY_MINUTES = 8 * 60;

/**
 * Formats an HHmm string as a 12-hour clock label (`"3:45 PM"`).
 * EN and FR both use en-US 12-hour style (OM-679) so French never shows a hybrid
 * `h` + a.m./p.m. label or a mismatched 24-hour clock next to English.
 *
 * @param hhmm Clock time as HHmm
 * @param _locale Active UI locale (signature kept for callers; clocks are 12h for both)
 */
export function formatHhmmAsDisplayTime(hhmm: string, _locale: PickupDisplayLocale): string {
  const minutes = hhmmToMinutes(hhmm);
  const hours24 = Math.floor(minutes / 60);
  const mins = minutes % 60;

  // en-US matches Figma ("3:45 PM") for both locales.
  const date = new Date(2000, 0, 1, hours24, mins, 0);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Builds the Figma date radio label: `"Today (June 18th)"` when the slot is today,
 * otherwise `"June 19, 2026"` (French uses Canadian French month names).
 *
 * @param todayLabelTemplate i18n template with `{{monthDay}}` (e.g. `Today ({{monthDay}})`)
 */
export function formatPickupDateLabel(
  dateIso: string,
  todayIso: string,
  locale: PickupDisplayLocale,
  todayLabelTemplate: string,
): string {
  const date = parseIsoDate(dateIso);
  if (dateIso === todayIso) {
    const monthDay = formatMonthDayWithOrdinal(date, locale);
    return todayLabelTemplate.replaceAll('{{monthDay}}', monthDay);
  }

  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

/**
 * Rounds an HHmm clock time upward to the next 15-minute interval.
 * Exact quarter-hours are kept unchanged (4:15 stays 4:15; 4:03 → 4:15; 4:16 → 4:30).
 */
export function roundUpToNextQuarterHourHhmm(hhmm: string): string {
  const total = hhmmToMinutes(hhmm);
  const remainder = total % TIME_STEP_MINUTES;
  if (remainder === 0) {
    return minutesToHhmm(total);
  }
  return minutesToHhmm(total + (TIME_STEP_MINUTES - remainder));
}

/**
 * Reads the Salesforce minimum pickup-window duration (minutes) from a validate slot.
 * Uses `cutOff` (mapped from Salesforce `cutoffWindowMins`) — never a hardcoded 60/120.
 */
export function resolveMinimumPickupWindowMinutes(slot: PickupAvailabilitySlot): number {
  const parsed =
    typeof slot.cutOff === 'number' ? slot.cutOff : Number.parseInt(String(slot.cutOff), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }
  return parsed;
}

/**
 * Maps validate `pickUps` slots into date radio options and seeds the default window
 * for the first eligible day (same-day From rounded up from current local time).
 */
export function mapValidatePickUpsToDateSelection(
  pickUps: PickupAvailabilitySlot[],
  options: {
    today: string;
    locale: PickupDisplayLocale;
    currentLocalHhmm: string;
    todayLabelTemplate: string;
  },
): PickupDateSelection {
  const slots = pickUps.slice(0, 2);
  if (slots.length === 0) {
    return {
      dates: [],
      selectedDate: null,
      windowFrom: null,
      windowTo: null,
    };
  }

  const dates = slots.map((slot) => ({
    date: slot.date,
    label: formatPickupDateLabel(
      slot.date,
      options.today,
      options.locale,
      options.todayLabelTemplate,
    ),
    isToday: slot.date === options.today,
  }));

  const defaults = selectFirstBookableDateDefaults(slots, {
    todayIso: options.today,
    currentLocalHhmm: options.currentLocalHhmm,
  });

  return {
    dates,
    selectedDate: defaults.selectedDate,
    windowFrom: defaults.windowFrom,
    windowTo: defaults.windowTo,
  };
}

/**
 * Picks the first slot (in API order, max two) with a bookable From/To window.
 * Falls back to the first slot with null windows when none are bookable (notice path).
 */
export function selectFirstBookableDateDefaults(
  pickUps: PickupAvailabilitySlot[],
  options: SelectPickupDateDefaultsOptions,
): { selectedDate: string | null; windowFrom: string | null; windowTo: string | null } {
  const slots = pickUps.slice(0, 2);
  if (slots.length === 0) {
    return { selectedDate: null, windowFrom: null, windowTo: null };
  }

  for (const slot of slots) {
    const defaults = selectPickupDateDefaults(pickUps, slot.date, options);
    if (defaults.windowFrom && defaults.windowTo) {
      return defaults;
    }
  }

  return selectPickupDateDefaults(pickUps, slots[0].date, options);
}

/**
 * Seeds `selectedDate` / window fields from the slot matching `date`.
 * Same-day From defaults to current local time rounded up; To defaults to From + SF min duration.
 */
export function selectPickupDateDefaults(
  pickUps: PickupAvailabilitySlot[],
  date: string,
  options: SelectPickupDateDefaultsOptions,
): { selectedDate: string | null; windowFrom: string | null; windowTo: string | null } {
  const slot = pickUps.find((entry) => entry.date === date);
  if (!slot) {
    return { selectedDate: null, windowFrom: null, windowTo: null };
  }

  const minWindowMinutes = resolveMinimumPickupWindowMinutes(slot);
  const isToday = slot.date === options.todayIso;
  const earliestFromHhmm = isToday
    ? roundUpToNextQuarterHourHhmm(options.currentLocalHhmm)
    : undefined;

  const provisionalFrom =
    earliestFromHhmm ?? minutesToHhmm(resolveDefaultEarliestFromMinutes(slot));
  const { fromOptions } = buildPickupWindowTimeOptions({
    latestPickupStartTime: slot.latestPickupStartTime,
    pickupWindowEndTime: slot.pickupWindowEndTime,
    selectedFrom: provisionalFrom,
    minWindowMinutes,
    earliestFromHhmm,
  });

  if (fromOptions.length === 0) {
    return { selectedDate: slot.date, windowFrom: null, windowTo: null };
  }

  const selectedFrom = fromOptions[0].value;
  const { toOptions } = buildPickupWindowTimeOptions({
    latestPickupStartTime: slot.latestPickupStartTime,
    pickupWindowEndTime: slot.pickupWindowEndTime,
    selectedFrom,
    minWindowMinutes,
    earliestFromHhmm,
  });
  const selection = resolvePickupWindowSelection({
    from: selectedFrom,
    to: null,
    fromOptions,
    toOptions,
  });

  return {
    selectedDate: slot.date,
    windowFrom: selection.from,
    windowTo: selection.to,
  };
}

/**
 * Builds From/To options for a validate slot using Salesforce min duration and same-day earliest From.
 */
export function buildWindowOptionsForSlot(
  slot: PickupAvailabilitySlot,
  selectedFrom: string,
  options: {
    todayIso: string;
    currentLocalHhmm: string;
    locale?: PickupDisplayLocale;
  },
): { fromOptions: PickupTimeOption[]; toOptions: PickupTimeOption[] } {
  const isToday = slot.date === options.todayIso;
  return buildPickupWindowTimeOptions({
    latestPickupStartTime: slot.latestPickupStartTime,
    pickupWindowEndTime: slot.pickupWindowEndTime,
    selectedFrom,
    minWindowMinutes: resolveMinimumPickupWindowMinutes(slot),
    earliestFromHhmm: isToday ? roundUpToNextQuarterHourHhmm(options.currentLocalHhmm) : undefined,
    locale: options.locale,
  });
}

/**
 * Builds from/to time options inside the selected day's window using Salesforce min duration.
 *
 * @param input Slot bounds, selected From, SF minimum minutes, and optional same-day earliest From
 */
export function buildPickupWindowTimeOptions(input: BuildPickupWindowTimeOptionsInput): {
  fromOptions: PickupTimeOption[];
  toOptions: PickupTimeOption[];
} {
  const locale = input.locale ?? 'en';
  const latestStart = hhmmToMinutes(input.latestPickupStartTime);
  const windowEnd = hhmmToMinutes(input.pickupWindowEndTime);
  const fromSelected = hhmmToMinutes(input.selectedFrom);
  const minWindow = input.minWindowMinutes;

  const earliestFrom = input.earliestFromHhmm
    ? Math.max(EARLIEST_READY_MINUTES, hhmmToMinutes(input.earliestFromHhmm))
    : EARLIEST_READY_MINUTES;

  const fromOptions: PickupTimeOption[] = [];
  for (let minutes = earliestFrom; minutes <= latestStart; minutes += TIME_STEP_MINUTES) {
    if (windowEnd - minutes < minWindow) {
      continue;
    }
    const value = minutesToHhmm(minutes);
    fromOptions.push({ value, label: formatHhmmAsDisplayTime(value, locale) });
  }

  const toOptions: PickupTimeOption[] = [];
  if (fromOptions.length === 0) {
    return { fromOptions, toOptions };
  }

  const earliestTo = fromSelected + minWindow;
  for (let minutes = earliestTo; minutes <= windowEnd; minutes += TIME_STEP_MINUTES) {
    const value = minutesToHhmm(minutes);
    toOptions.push({ value, label: formatHhmmAsDisplayTime(value, locale) });
  }

  if (
    toOptions.length === 0 ||
    !toOptions.some((option) => option.value === input.pickupWindowEndTime)
  ) {
    if (windowEnd - fromSelected >= minWindow) {
      const endValue = input.pickupWindowEndTime;
      if (!toOptions.some((option) => option.value === endValue)) {
        toOptions.push({
          value: endValue,
          label: formatHhmmAsDisplayTime(endValue, locale),
        });
      }
    }
  }

  return { fromOptions, toOptions };
}

/**
 * Reconciles a From/To selection against option lists: keeps To when still valid,
 * otherwise resets to the earliest selectable To. Empty From options → no valid window.
 */
export function resolvePickupWindowSelection(input: {
  from: string | null;
  to: string | null;
  fromOptions: PickupTimeOption[];
  toOptions: PickupTimeOption[];
}): PickupWindowSelectionResult {
  if (input.fromOptions.length === 0) {
    return { from: null, to: null, hasValidWindow: false };
  }

  const from =
    input.from && input.fromOptions.some((option) => option.value === input.from)
      ? input.from
      : input.fromOptions[0].value;

  if (input.toOptions.length === 0) {
    return { from, to: null, hasValidWindow: false };
  }

  const to =
    input.to && input.toOptions.some((option) => option.value === input.to)
      ? input.to
      : input.toOptions[0].value;

  return { from, to, hasValidWindow: true };
}

/** Converts HHmm to minutes since midnight. */
export function hhmmToMinutes(hhmm: string): number {
  const hours = Number(hhmm.slice(0, 2));
  const minutes = Number(hhmm.slice(2, 4));
  return hours * 60 + minutes;
}

/** Converts minutes since midnight to a zero-padded HHmm string. */
export function minutesToHhmm(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${pad2(hours)}${pad2(minutes)}`;
}

/** Earliest From for a non-today slot: 08:00 local. */
function resolveDefaultEarliestFromMinutes(_slot: PickupAvailabilitySlot): number {
  return EARLIEST_READY_MINUTES;
}

function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}

function parseIsoDate(dateIso: string): Date {
  const [year, month, day] = dateIso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Formats month + day with an English ordinal suffix for the Figma "Today (June 18th)" pattern.
 * French omits the ordinal and uses a day-month order.
 */
function formatMonthDayWithOrdinal(date: Date, locale: PickupDisplayLocale): string {
  if (locale === 'fr') {
    return new Intl.DateTimeFormat('fr-CA', { day: 'numeric', month: 'long' }).format(date);
  }

  const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
  const day = date.getDate();
  return `${month} ${day}${ordinalSuffix(day)}`;
}

function ordinalSuffix(day: number): string {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}
